/**
 * <quick-discount> — "Quick-Apply"-Rabatt-Banner.
 *
 * Wendet einen voreingestellten Rabattcode GENAU SO an wie der Apply-Button im
 * Warenkorb -- aber eigenstaendig, OHNE das <cart-discounts>-Element zu nutzen:
 * dieselbe Anfrage (POST /cart/update.js, Feld "discount") und dasselbe
 * `discount:updated`-Event. Dadurch laeuft die site-weite Preisreduzierung
 * (discount-sitewide.js) automatisch ueber den bestehenden Pfad.
 *
 * Ablauf nach Klick (oder Auto-Apply beim Laden):
 *   1. Bestehende anwendbare Codes lesen (/cart.js) und den neuen anhaengen.
 *   2. POST /cart/update.js mit allen Codes.
 *   3. Ist der Code anwendbar (applicable) -> `discount:updated` feuern + Erfolg;
 *      sonst ehrliche Fehlermeldung (z. B. leerer Warenkorb / ungueltiger Code).
 *   4. Success-/Error-Meldung im selben Element, danach Banner ausblenden.
 */
(function () {
  'use strict';
  if (!window.customElements || customElements.get('quick-discount')) return;

  // Muss mit MANUAL_KEY in discount-sitewide.js uebereinstimmen.
  var MANUAL_KEY = 'sitewideDiscountManual';

  // Direkter localStorage-Zugriff (load-order-sicher: discount-sitewide.js ist
  // beim Auto-Apply evtl. noch nicht geladen).
  function readManual() {
    try {
      var raw = localStorage.getItem(MANUAL_KEY);
      if (!raw) return null;
      var o = JSON.parse(raw);
      return o && o.code ? o : null;
    } catch (e) {
      return null;
    }
  }

  function writeManual(code, percent) {
    try { localStorage.setItem(MANUAL_KEY, JSON.stringify({ code: code, percent: percent })); } catch (e) {}
  }

  class QuickDiscount extends HTMLElement {
    connectedCallback() {
      this.code = (this.getAttribute('data-code') || '').trim();
      this.percent = parseFloat(this.getAttribute('data-percent')) || 0;
      this.successMsg = this.getAttribute('data-success') || 'Rabatt aktiviert';
      this.errorMsg = this.getAttribute('data-error') || 'Code konnte nicht angewendet werden';
      var delay = parseInt(this.getAttribute('data-hide-delay'), 10);
      this.hideDelay = isNaN(delay) ? 4 : delay;

      this.inner = this.querySelector('.quick-discount__inner');
      this.button = this.querySelector('.quick-discount__button');
      this.messageEl = this.querySelector('.quick-discount__message');
      this.busy = false;
      this.done = false;

      // Bereits via Cookie vorgemerkt (leerer Warenkorb)? -> Banner nicht zeigen.
      // (Der bei gefuelltem Warenkorb angewendete Fall wird schon serverseitig
      //  in Liquid ausgeblendet.)
      var manual = readManual();
      if (manual && manual.code && this.code &&
          manual.code.toUpperCase() === this.code.toUpperCase()) {
        this.removeSelf();
        return;
      }

      // Bereits IRGENDEIN Bestellrabatt aktiv (anderer Code, automatischer/
      // Affiliate-Rabatt wie GoAffPro)? -> Banner nicht zeigen. Synchroner
      // Schnell-Check ueber den gecachten Prozentsatz (kein Flicker); der
      // /cart.js-Check unten ist die Autoritaet und deckt auch nicht-prozentuale
      // sowie automatische Rabatte ab.
      try {
        if (window.SitewideDiscount && window.SitewideDiscount.getPercent &&
            window.SitewideDiscount.getPercent() > 0) {
          this.removeSelf();
          return;
        }
      } catch (e) {}

      if (this.button) {
        this.button.addEventListener('click', this.apply.bind(this));
      }

      // Falls spaeter ein Bestellrabatt aktiv wird (Cart-Drawer, Referral beim
      // Add-to-Cart) -> Banner ausblenden. NICHT waehrend/nach dem EIGENEN
      // Anwenden (busy/done), damit Erfolgsmeldung + Auto-Hide ungestoert laufen.
      var self = this;
      this._onSitewideChange = function (e) {
        if (self.busy || self.done) return;
        if (e && e.detail && e.detail.percent > 0) self.removeSelf();
      };
      document.addEventListener('sitewide-discount:changed', this._onSitewideChange);

      // Autoritative Pruefung am Warenkorb (deckt bfcache/veraltete Seiten sowie
      // nicht-prozentuale/automatische Rabatte ab). Auto-Apply erst danach und
      // nur, wenn KEIN Bestellrabatt aktiv ist.
      this.guardThenAutoApply();
    }

    /* /cart.js lesen: liegt schon ein Bestellrabatt vor -> ausblenden, sonst
       ggf. Auto-Apply. */
    guardThenAutoApply() {
      var self = this;
      var autoApply = this.getAttribute('data-auto') === 'true' && !!this.code;
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          if (self.busy || self.done) return; // eigener Apply laeuft bereits
          if (self.hasOrderDiscount(cart)) {
            self.removeSelf();
            return;
          }
          if (autoApply) self.apply();
        })
        .catch(function () {
          // Warenkorb nicht lesbar -> Liquid-Check war massgeblich; Auto-Apply
          // wie bisher zulassen.
          if (autoApply && !self.busy && !self.done) self.apply();
        });
    }

    /* Liegt am Warenkorb bereits irgendein Bestellrabatt? (Code oder automatisch.) */
    hasOrderDiscount(cart) {
      if (!cart) return false;
      if (cart.total_discount > 0) return true;
      if ((cart.cart_level_discount_applications || []).length > 0) return true;
      // Vorgemerkter Code (z. B. Referral-/Partner-Link via /discount/CODE) liegt
      // schon beim Laden in discount_codes vor -- bei leerem Warenkorb mit
      // applicable:false, da noch nichts zu rabattieren ist. Auch dann ist ein
      // Rabatt aktiv -> Banner ausblenden, ohne auf das naechste Cart-Update zu
      // warten.
      if ((cart.discount_codes || []).some(function (d) { return d && d.code; })) return true;
      var items = cart.items || [];
      for (var i = 0; i < items.length; i++) {
        var it = items[i] || {};
        var allocs = it.discount_allocations || it.line_level_discount_allocations || [];
        if (allocs.length > 0) return true;
      }
      return false;
    }

    /* Banner-Container entfernen (wie beim Cookie-/Rabatt-Check). */
    removeSelf() {
      if (this._onSitewideChange) {
        document.removeEventListener('sitewide-discount:changed', this._onSitewideChange);
        this._onSitewideChange = null;
      }
      var section = this.closest('.product-block') || this;
      if (section && section.parentNode) section.parentNode.removeChild(section);
    }

    apply() {
      if (!this.code || this.busy || this.done) return;
      this.busy = true;
      this.classList.add('quick-discount--loading');

      var self = this;
      var upper = this.code.toUpperCase();
      var updateUrl = (window.theme && window.theme.routes && window.theme.routes.cart_update_url) || '/cart/update';

      var isApplied = function (cart) {
        return ((cart && cart.discount_codes) || []).some(function (d) {
          return (d.code || '').toUpperCase() === upper && d.applicable !== false;
        });
      };

      // 1) Bestehende (anwendbare) Codes lesen, damit sie nicht ueberschrieben
      //    werden -- exakt wie der Cart-Drawer es tut.
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var codes = (cart.discount_codes || [])
            .filter(function (d) { return d.applicable !== false; })
            .map(function (d) { return d.code; });
          if (!codes.some(function (c) { return c.toUpperCase() === upper; })) {
            codes.push(self.code);
          }

          // 2) GLEICHE Anfrage wie der Cart-Drawer (cart/update.js, Feld "discount").
          return fetch(updateUrl + '.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ discount: codes.join(',') })
          }).then(function (r) { return r.json(); });
        })
        .then(function (cart) {
          if (isApplied(cart)) {
            // 3) Erfolg am (gefuellten) Warenkorb. GLEICHES Event wie der
            //    Cart-Drawer -> site-weite Preise + Cart ueber den bestehenden
            //    Pfad (discount-sitewide.js) aktualisieren.
            if (typeof dispatchCustomEvent === 'function') {
              dispatchCustomEvent('discount:updated', { discountCodes: cart.discount_codes, cart: cart });
            } else if (window.SitewideDiscount && window.SitewideDiscount.refresh) {
              window.SitewideDiscount.refresh();
            }
            self.succeed();
            return;
          }
          if (cart.item_count === 0) {
            // Leerer Warenkorb kann den Code nicht validieren -> via Cookie
            // vormerken (greift automatisch beim Hinzufuegen von Artikeln) und
            // die Preise sofort reduziert anzeigen.
            self.preApply();
            return;
          }
          // Warenkorb hat Artikel, Code aber nicht anwendbar -> echter Fehler.
          self.fail();
        })
        .catch(function () { self.fail(); });
    }

    /* Leerer Warenkorb: Code via Shopify-Discount-Cookie vormerken. */
    preApply() {
      var self = this;
      fetch('/discount/' + encodeURIComponent(this.code), { credentials: 'same-origin' })
        .catch(function () {})
        .then(function () {
          if (self.percent > 0) {
            // Persistieren (auch falls discount-sitewide.js noch nicht geladen).
            writeManual(self.code, self.percent);
            if (window.SitewideDiscount && window.SitewideDiscount.setManual) {
              window.SitewideDiscount.setManual(self.code, self.percent);
            }
          }
          self.succeed();
        });
    }

    showMessage(text, type) {
      if (this.inner) this.inner.setAttribute('hidden', '');
      if (this.messageEl) {
        this.messageEl.textContent = text;
        this.messageEl.removeAttribute('hidden');
      }
      this.classList.remove('quick-discount--loading');
      this.classList.add('quick-discount--' + type);
    }

    succeed() {
      this.busy = false;
      this.done = true;
      this.showMessage(this.successMsg, 'success');
      this.scheduleHide();
    }

    fail() {
      this.busy = false;
      this.showMessage(this.errorMsg, 'error');
      this.scheduleHide();
    }

    scheduleHide() {
      if (!(this.hideDelay > 0)) return;
      var section = this.closest('.product-block') || this;
      var self = this;
      this._hideTimer = setTimeout(function () {
        section.classList.add('quick-discount-collapsing');
        section.style.maxHeight = section.scrollHeight + 'px';
        // Reflow erzwingen, damit die Transition startet.
        void section.offsetHeight;
        section.style.maxHeight = '0px';
        section.style.opacity = '0';
        section.style.paddingTop = '0';
        section.style.paddingBottom = '0';
        section.style.marginTop = '0';
        section.style.marginBottom = '0';
        setTimeout(function () {
          if (section && section.parentNode) section.parentNode.removeChild(section);
        }, 450);
        self._hideTimer = null;
      }, this.hideDelay * 1000);
    }
  }

  customElements.define('quick-discount', QuickDiscount);
})();
